// Stripe.js authority.
//
// The publishable key is fetched at runtime from the same
// kinsvault-subscription Edge Function that creates the
// SetupIntent and subscription.
//
// Stripe's official loader manages script loading, deduplication,
// readiness, and retries more reliably than a custom script tag.

import {
  loadStripe as loadOfficialStripe,
  type Stripe,
} from '@stripe/stripe-js';

import { supabase } from '@/lib/supabase';

interface StripeKeyProjection {
  publishableKey: string;
  backendAccountId: string | null;
}

let stripePromise: Promise<Stripe | null> | null =
  null;

async function fetchPublishableKey():
  Promise<StripeKeyProjection> {
  const { data, error } =
    await supabase.functions.invoke(
      'kinsvault-subscription',
      {
        body: {
          action: '__pk__',
        },
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        'Failed to fetch Stripe publishable key.',
    );
  }

  if (data?.error) {
    throw new Error(String(data.error));
  }

  const publishableKey =
    data?.publishableKey;

  if (
    typeof publishableKey !== 'string' ||
    !/^pk_(live|test)_/.test(
      publishableKey,
    )
  ) {
    throw new Error(
      'Backend did not return a valid Stripe publishable key.',
    );
  }

  return {
    publishableKey,
    backendAccountId:
      typeof data?.backendAccountId ===
      'string'
        ? data.backendAccountId
        : null,
  };
}

export function loadStripe():
  Promise<Stripe | null> {
  if (stripePromise) {
    return stripePromise;
  }

  stripePromise = (
    async () => {
      const {
        publishableKey,
        backendAccountId,
      } = await fetchPublishableKey();

      console.log(
        '[stripe] official loader init',
        {
          pk_prefix:
            publishableKey.slice(0, 12),
          pk_length:
            publishableKey.length,
          backendAccountId,
        },
      );

      // No Connect account override is used.
      // SetupIntent and publishable key belong to the same
      // standalone Stripe account.
      return await loadOfficialStripe(
        publishableKey,
      );
    }
  )().catch((error) => {
    stripePromise = null;
    throw error;
  });

  return stripePromise;
}
